import React, { ComponentType } from 'react';

import { Main } from '@atlaskit/navigation-system/layout/main';
import { media } from '@atlaskit/primitives';
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';

import { GriffinMainGrid } from '@townsquare/griffin-main-grid';
import { GriffinMainGridContent } from '@townsquare/griffin-main-grid/content';
import { GriffinMainGridHeader } from '@townsquare/griffin-main-grid/header';
import { PreviewPanelContainer } from '@townsquare/preview-panels/previewPanelContainer';
import { AggRelayEnvironmentProvider } from '@townsquare/relay-utils/agg-environment';
import { RouteComponent } from '@townsquare/router/RouteComponent';
import { HomepageRoute, LayoutType } from '@townsquare/router/route-types';
import { useRouter } from '@townsquare/router/useRouter';
import { RovoButton } from '@townsquare/rovo-ai-button';
import { useRightSidebarState } from '@townsquare/sidebar-panel/right-sidebar';
import { isEntryPointPanelSupported } from '@townsquare/sidebar-panel/utils';
import { LegacySidebarPanel } from '@townsquare/sidebar-panel-utils/legacy-sidebar-panel';
import { expVal } from '@townsquare/stat-sig';
import { TOWNSQUARE_FSJ_ROVO_BUTTON_NUDGE } from '@townsquare/stat-sig/experiments';
import { fg } from '@townsquare/stat-sig/gate';

import {
	useIsOnFullPageRovoChat,
	useIsOnRovoSearch,
	useIsOnFullPageSearch,
} from '../utils/route-utils';

import { GriffinResponsiveAside } from './GriffinResponsiveAside';

// Expected with for main column CONTENT is 1232px.
const MAIN_CONTENT_LX_WIDTH = '1232px';

type AsideType = Exclude<HomepageRoute['aside'], undefined>;

type GriffinLayoutProps = {
	aside: AsideType | undefined;
	layout: LayoutType;
};

/**
 * Additional default styling to conform to a grid structure
 */
const defaultMainContentStyles = xcss({
	position: 'relative',
	marginInlineEnd: 'auto',
	marginInlineStart: 'auto',
	// We want the column content width to be the set width, with padding and margin added on top.
	boxSizing: 'content-box',
	maxWidth: MAIN_CONTENT_LX_WIDTH,
	marginBlock: 'space.400',
	paddingInline: 'space.200',
	[media.above.md]: {
		paddingInline: 'space.400',
	},
});

const mainContentWithAsideStyles = xcss({
	// If there is an aside column, we don't want the max page width to exceed 800px.
	maxWidth: '800px',
});

const GriffinMainContent = (props: GriffinLayoutProps) => {
	const useDefaultStyle = props.layout === 'home';

	return (
		<GriffinMainGridContent>
			{useDefaultStyle ? (
				<Box
					xcss={[defaultMainContentStyles, props.aside ? mainContentWithAsideStyles : undefined]}
				>
					<RouteComponent />
				</Box>
			) : (
				<RouteComponent />
			)}
		</GriffinMainGridContent>
	);
};

const GriffinWithAside = (
	props: GriffinLayoutProps & { aside: AsideType; hasNewHeader: boolean },
) => {
	const AsideComponent = props.aside;

	return (
		<>
			<GriffinMainContent {...props} />
			<GriffinResponsiveAside hasNewHeader={props.hasNewHeader}>
				<AsideComponent />
			</GriffinResponsiveAside>
		</>
	);
};

const AggRelayProvider = ({ children }: { children: React.ReactNode }) => (
	<AggRelayEnvironmentProvider>{children}</AggRelayEnvironmentProvider>
);

export const GriffinLayout = ({
	PlatformSidebarPanel,
}: {
	PlatformSidebarPanel: ComponentType;
}): React.JSX.Element => {
	const { currentPanel } = useRightSidebarState();
	const [routerState] = useRouter();

	const route = routerState.route as HomepageRoute;
	const layout = route.nav4Layout ?? (route?.layout || 'home');

	const HeaderComponent = route.header;
	const AggContainer = route.aggContainer;

	const isOnFullPageRovoChat = useIsOnFullPageRovoChat();
	const isOnFullPageSearch = useIsOnFullPageSearch();
	const isOnRovoSearch = useIsOnRovoSearch();

	const shouldShowRovoButton = (routeName: string) => {
		if (routeName === 'hometab') {
			// must not be on home tab page to show the button
			return false;
		}

		if (expVal(TOWNSQUARE_FSJ_ROVO_BUTTON_NUDGE, 'isEnabled', true)) {
			// must not be on full page chat if experiment enabled since the experiment adds rovo button and nudge in full page search
			return !isOnFullPageRovoChat;
		}

		// must be on Rovo search page to show the button
		// or not on Rovo pages already
		return (!isOnFullPageRovoChat && !isOnFullPageSearch) || isOnRovoSearch;
	};

	const showRovoButton = shouldShowRovoButton(routerState.route.name);
	const shouldShowSidebarPanel = currentPanel && isEntryPointPanelSupported(currentPanel);

	const content = route.aside ? (
		<GriffinWithAside layout={layout} aside={route?.aside} hasNewHeader={!!route.header} />
	) : (
		<GriffinMainContent layout={layout} aside={route?.aside} />
	);

	const SidebarPanel = shouldShowSidebarPanel ? (
		<PlatformSidebarPanel />
	) : (
		<LegacySidebarPanel currentPanel={currentPanel} />
	);

	const RelayProvider = route.relayEnvironment === 'agg' ? AggRelayProvider : React.Fragment;

	if (layout === 'custom') {
		return (
			<RelayProvider>
				<Main testId={routerState.route.name}>
					<RouteComponent />
					{showRovoButton && <RovoButton />}
				</Main>
				{SidebarPanel}
				<PreviewPanelContainer />
			</RelayProvider>
		);
	}

	const LayoutContent = (
		<RelayProvider>
			<Main testId={routerState.route.name}>
				<GriffinMainGrid>
					{HeaderComponent && (
						<GriffinMainGridHeader>
							<HeaderComponent />
						</GriffinMainGridHeader>
					)}
					{content}
					{showRovoButton && <RovoButton />}
				</GriffinMainGrid>
			</Main>
			{SidebarPanel}
			<PreviewPanelContainer />
		</RelayProvider>
	);

	if (fg('townsquare_enable_combined_agg_query_sidebar') && AggContainer) {
		return <AggContainer>{LayoutContent}</AggContainer>;
	}

	return LayoutContent;
};
